import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Donation: React.FC = () => {
    // State for form fields
    const [formData, setFormData] = useState({
        fullName: '',
        dob: '',
        email: '',
        mobile: '',
        address: '',
        pincode: '',
        city: '',
        state: '',
        pan: '',
        declaration: false
    });

    // State for errors
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [, setIsSubmitted] = useState(false);

    // List of Indian States
    const indianStates = [
        "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
        "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
        "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
        "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
        "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
        "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep", "Delhi", "Puducherry",
        "Ladakh", "Jammu and Kashmir"
    ];

    // Validation Functions
    const validate = () => {
        let tempErrors: Record<string, string> = {};

        if (!formData.fullName.trim()) tempErrors.fullName = "Full Name is required";

        if (!formData.dob) tempErrors.dob = "Date of Birth is required";

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email) tempErrors.email = "Email is required";
        else if (!emailRegex.test(formData.email)) tempErrors.email = "Invalid email format";

        const mobileRegex = /^[0-9]{10}$/;
        if (!formData.mobile) tempErrors.mobile = "Mobile Number is required";
        else if (!mobileRegex.test(formData.mobile)) tempErrors.mobile = "Mobile Number must be 10 digits";

        if (!formData.address.trim()) tempErrors.address = "Address is required";

        const pincodeRegex = /^[0-9]{6}$/;
        if (!formData.pincode) tempErrors.pincode = "Pincode is required";
        else if (!pincodeRegex.test(formData.pincode)) tempErrors.pincode = "Pincode must be 6 digits";

        if (!formData.city.trim()) tempErrors.city = "City is required";

        if (!formData.state) tempErrors.state = "State is required";

        const panRegex = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
        if (!formData.pan) tempErrors.pan = "PAN Number is required";
        else if (!panRegex.test(formData.pan.toUpperCase())) tempErrors.pan = "Invalid PAN format (e.g., ABCDE1234F)";

        if (!formData.declaration) tempErrors.declaration = "You must accept the declaration";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }

        // Clear error when user types
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            console.log("Form Submitted", formData);
            setIsSubmitted(true);
            // Here you would typically send data to backend
            alert("Thank you for your donation details! We will contact you shortly.");
            // Reset form
            setFormData({
                fullName: '',
                dob: '',
                email: '',
                mobile: '',
                address: '',
                pincode: '',
                city: '',
                state: '',
                pan: '',
                declaration: false
            });
            setIsSubmitted(false);
        } else {
            // Scroll to first error
            const firstError = document.querySelector('.text-red-500');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-poppins">
            {/* Hero Section with Image */}
            <div className="relative w-full h-[300px] md:h-[400px]">
                <img
                    src="/backend log/donate1.png"
                    alt="Donation Hero"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 flex flex-col justify-center items-center text-center px-4">
                    <h2 className="text-5xl font-extrabold text-white sm:text-5xl shadow-sm">
                        Make a Donation
                    </h2>
                    <p className="mt-4 text-lg text-white font-medium max-w-2xl shadow-sm">
                        Join us in making a difference. Your contribution helps us empower communities.
                    </p>
                </div>
            </div>
            <div className="max-w-2xl mx-auto relative z-10 -mt-24">
                <div className="bg-white py-8 px-4 shadow-xl rounded-2xl sm:px-10 border-t-4 border-secondary">
                    <form className="space-y-6" onSubmit={handleSubmit}>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Full Name */}
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        name="fullName"
                                        id="fullName"
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.fullName ? 'border-red-500' : 'pl-3'}`}
                                        placeholder="John Doe"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
                            </div>

                            {/* Date of Birth */}
                            <div>
                                <label htmlFor="dob" className="block text-sm font-medium text-gray-700">
                                    Date of Birth <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="date"
                                        name="dob"
                                        id="dob"
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.dob ? 'border-red-500' : 'pl-3'}`}
                                        value={formData.dob}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.dob && <p className="mt-1 text-xs text-red-500">{errors.dob}</p>}
                            </div>

                            {/* Email Address */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.email ? 'border-red-500' : 'pl-3'}`}
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                            </div>

                            {/* Mobile Number */}
                            <div>
                                <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        name="mobile"
                                        id="mobile"
                                        maxLength={10}
                                        // Ensure validation prevents non-numeric input if standard text input
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.mobile ? 'border-red-500' : 'pl-3'}`}
                                        placeholder="9876543210"
                                        value={formData.mobile}
                                        onChange={(e) => {
                                            const re = /^[0-9\b]+$/;
                                            if (e.target.value === '' || re.test(e.target.value)) {
                                                handleChange(e);
                                            }
                                        }}
                                    />
                                </div>
                                {errors.mobile && <p className="mt-1 text-xs text-red-500">{errors.mobile}</p>}
                            </div>
                        </div>

                        {/* Address */}
                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                                Address <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <textarea
                                    name="address"
                                    id="address"
                                    rows={3}
                                    className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.address ? 'border-red-500' : 'pl-3'}`}
                                    placeholder="Street address, Apartment, Suite, etc."
                                    value={formData.address}
                                    onChange={handleChange}
                                />
                            </div>
                            {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Pincode */}
                            <div>
                                <label htmlFor="pincode" className="block text-sm font-medium text-gray-700">
                                    Pincode <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        name="pincode"
                                        id="pincode"
                                        maxLength={6}
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.pincode ? 'border-red-500' : 'pl-3'}`}
                                        placeholder="600001"
                                        value={formData.pincode}
                                        onChange={(e) => {
                                            const re = /^[0-9\b]+$/;
                                            if (e.target.value === '' || re.test(e.target.value)) {
                                                handleChange(e);
                                            }
                                        }}
                                    />
                                </div>
                                {errors.pincode && <p className="mt-1 text-xs text-red-500">{errors.pincode}</p>}
                            </div>

                            {/* City */}
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        name="city"
                                        id="city"
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.city ? 'border-red-500' : 'pl-3'}`}
                                        placeholder="City Name"
                                        value={formData.city}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
                            </div>

                            {/* State Dropdown */}
                            <div>
                                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                                    State <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <select
                                        name="state"
                                        id="state"
                                        className={`focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 bg-white ${errors.state ? 'border-red-500' : 'pl-3'}`}
                                        value={formData.state}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select State</option>
                                        {indianStates.map((state) => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                {errors.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
                            </div>

                            {/* PAN Number */}
                            <div>
                                <label htmlFor="pan" className="block text-sm font-medium text-gray-700">
                                    PAN Number <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        name="pan"
                                        id="pan"
                                        maxLength={10}
                                        className={`uppercase focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md py-3 pl-3 ${errors.pan ? 'border-red-500' : 'pl-3'}`}
                                        placeholder="ABCDE1234F"
                                        value={formData.pan}
                                        onChange={(e) => {
                                            const val = e.target.value.toUpperCase();
                                            if (val.length <= 10) {
                                                setFormData({ ...formData, pan: val });
                                                if (errors.pan) setErrors({ ...errors, pan: '' });
                                            }
                                        }}
                                    />
                                </div>
                                {errors.pan && <p className="mt-1 text-xs text-red-500">{errors.pan}</p>}
                            </div>
                        </div>

                        {/* Declaration Checkbox */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        id="declaration"
                                        name="declaration"
                                        type="checkbox"
                                        className={`focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded ${errors.declaration ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        checked={formData.declaration}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label htmlFor="declaration" className="font-medium text-gray-700">Declaration <span className="text-red-500">*</span></label>
                                    <p className="text-gray-500 mt-1 leading-relaxed">
                                        I hereby declare that I am a citizen of India, making this donation out of my own funds. The information provided above is correct to the best of my knowledge. I understand that all further communications will be done using the contact details provided above.
                                    </p>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Your personal details (including PAN for Section 80G tax exemption certification) are collected and processed in strict adherence to India&apos;s DPDP Act, 2023. For details on your statutory rights and retention schedules, view our{' '}
                                        <Link to="/privacy-policy" className="text-primary font-semibold underline hover:text-primary-dark" target="_blank" rel="noopener noreferrer">
                                            Privacy Policy &amp; Notice
                                        </Link>.
                                    </p>
                                    {errors.declaration && <p className="mt-1 text-xs text-red-500 font-bold">{errors.declaration}</p>}
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 flex justify-center">
                            <button
                                type="submit"
                                className="w-auto px-12 py-3 border border-transparent rounded-full shadow-premium text-sm font-bold text-white bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-light hover:to-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transform transition-all duration-300 hover:-translate-y-1"
                            >
                                Donate Now
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Donation;
